if (!customElements.get('product-upsells')) {
  customElements.define(
    'product-upsells',
    class ProductUpsells extends HTMLElement {
      constructor() {
        super();

        this.sliderInstance = false;
      }

      connectedCallback() {
        this.items = this.querySelector('[data-product-upsells-items]');
        this.viewport = this.querySelector('.product-upsells__viewport');
        this.controls = this.querySelector('.product-upsells__controls');

        if (!this.items || !this.viewport) return;

        if (this.items.children.length > 0) {
          this.initSlider();
          return;
        }

        this.loadSkuProducts();
      }

      async loadSkuProducts() {
        const skus = this.getSkus();
        if (!skus.length) {
          this.hideBlock();
          return;
        }

        const cards = [];

        for (const sku of skus) {
          const item = await this.findVariantBySku(sku);
          if (item) cards.push(this.renderCard(item));
        }

        if (!cards.length) {
          this.hideBlock();
          return;
        }

        this.items.innerHTML = cards.join('');
        this.initSlider();
      }

      getSkus() {
        const script = this.querySelector('[data-product-upsells-skus]');
        if (!script) return [];

        try {
          const source = JSON.parse(script.textContent);
          const values = Array.isArray(source) ? source : [source];

          return values
            .flatMap((value) => {
              if (value && typeof value === 'object') {
                return [value.sku || value.value || ''];
              }

              return String(value || '').split(/[,;\n]/);
            })
            .map((value) => value.trim())
            .filter(Boolean);
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      async findVariantBySku(sku) {
        const productResults = await this.searchProducts(sku);
        const visited = new Set();
        const normalizedSku = sku.trim().toLowerCase();

        for (const result of productResults) {
          const productUrl = result?.url?.split('?')[0];
          if (!productUrl || visited.has(productUrl)) continue;

          visited.add(productUrl);
          const product = await this.fetchProduct(productUrl);
          const variant = product?.variants?.find((item) => item.sku?.trim().toLowerCase() === normalizedSku);

          if (variant) {
            return { product, variant };
          }
        }

        return null;
      }

      async searchProducts(sku) {
        const suggestUrl = `${FoxTheme.routes.predictive_search_url}${
          FoxTheme.routes.predictive_search_url.endsWith('.json') ? '' : '.json'
        }`;
        const params = new URLSearchParams({
          q: sku,
          'resources[type]': 'product',
          'resources[limit]': '8',
          'resources[options][fields]': 'title,variants.sku,vendor,product_type',
        });
        let response = await fetch(`${suggestUrl}?${params.toString()}`);
        if (!response.ok) {
          params.delete('resources[options][fields]');
          response = await fetch(`${suggestUrl}?${params.toString()}`);
        }
        if (!response.ok) return [];

        try {
          const payload = await response.json();
          return payload?.resources?.results?.products || [];
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      async fetchProduct(productUrl) {
        const response = await fetch(`${productUrl}.js`);
        if (!response.ok) return null;

        return response.json();
      }

      renderCard({ product, variant }) {
        const url = `${product.url}?variant=${variant.id}`;
        const image = this.getVariantImage(product, variant);
        const compareAtPrice = Number(variant.compare_at_price || 0);
        const price = Number(variant.price || product.price || 0);
        const title = this.escape(product.title || variant.name || '');
        const viewLabel = this.escape(this.dataset.viewLabel || 'View');
        const comparePriceHtml =
          compareAtPrice > price
            ? `<s>${FoxTheme.Currency.formatMoney(compareAtPrice, FoxTheme.settings.moneyFormat)}</s>`
            : '';

        return `
          <div class="swiper-slide product-upsells__slide">
            <article class="product-upsell-card">
              <a href="${url}" class="product-upsell-card__media" tabindex="-1" aria-label="${title}">
                ${image ? `<img src="${image}" alt="${title}" loading="lazy">` : ''}
              </a>
              <div class="product-upsell-card__content">
                <a href="${url}" class="product-upsell-card__title reversed-link">${title}</a>
                <div class="product-upsell-card__price">
                  ${comparePriceHtml}
                  <span>${FoxTheme.Currency.formatMoney(price, FoxTheme.settings.moneyFormat)}</span>
                </div>
                <a href="${url}" class="product-upsell-card__button btn btn--primary">${viewLabel}</a>
              </div>
            </article>
          </div>
        `;
      }

      getVariantImage(product, variant) {
        if (variant.featured_image?.src) return variant.featured_image.src;
        if (variant.featured_image) return variant.featured_image;
        if (product.featured_image) return product.featured_image;
        if (product.images?.length) return product.images[0];

        return '';
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object' || this.items.children.length === 0) return;

        this.sliderInstance = new window.FoxTheme.Carousel(this.viewport, {
          slidesPerView: 1.2,
          spaceBetween: 16,
          loop: false,
          threshold: 2,
          navigation: {
            nextEl: this.controls?.querySelector('.swiper-button-next'),
            prevEl: this.controls?.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.controls?.querySelector('.swiper-pagination'),
            type: 'fraction',
          },
        });
        this.sliderInstance.init();
      }

      hideBlock() {
        this.closest('.product__block')?.classList.add('hidden');
      }

      escape(value) {
        const element = document.createElement('span');
        element.textContent = value;
        return element.innerHTML;
      }
    }
  );
}
